import { differenceInCalendarDays } from 'date-fns'
import { AlertTriangle, CreditCard } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { hasRole } from '@/lib/hasRole'
import { cn } from '@/lib/utils'
import { Role } from '@/types/User'

const WARNING_DAYS = 14

export const SubscriptionBanner = () => {
  const { user } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'SubscriptionBanner',
  })

  const { t } = useTranslation(undefined, {
    keyPrefix: 'subscription.banner',
  })

  if (!user || hasRole(user.roles, Role.admin)) {
    return null
  }

  const isExpired = user.sia === false

  let daysLeft: number | null = null

  if (!isExpired && user.subscriptionEndsAt) {
    daysLeft = differenceInCalendarDays(new Date(user.subscriptionEndsAt), new Date())

    if (daysLeft > WARNING_DAYS) {
      return null
    }
  }

  if (!isExpired && daysLeft === null) {
    return null
  }

  const canManageSubscription = hasRole(user.roles, [Role.owner, Role.manager])

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-b px-4 py-2 text-sm',
        isExpired
          ? 'bg-destructive/10 border-destructive/30 text-destructive'
          : 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200',
      )}
    >
      <AlertTriangle className="size-4 shrink-0" />
      <span className="font-medium">
        {isExpired
          ? t('expired')
          : daysLeft === 0
            ? t('expiresToday')
            : t('expiresIn', { count: daysLeft ?? 0 })}
      </span>
      {canManageSubscription ? (
        <Button asChild size="sm" variant={isExpired ? 'destructive' : 'default'}>
          <Link to="/subscription">
            <CreditCard />
            {t('renew')}
          </Link>
        </Button>
      ) : (
        <span>{t('contactOwner')}</span>
      )}
    </div>
  )
}

SubscriptionBanner.displayName = 'SubscriptionBanner'
